import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Filler, BubbleController } from 'chart.js';
import { Bar, Pie, Line, Doughnut, Bubble } from 'react-chartjs-2';
import { PieChart, BarChart3, LineChart, Activity, Grid3X3, ChevronDown, ChevronUp, ZoomIn, RotateCcw, Maximize2, TrendingUp } from 'lucide-react';
import axiosInstance from '../services/axiosInstance';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Filler, BubbleController);

const Analytics = () => {
  const { user } = useOutletContext();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSector, setExpandedSector] = useState(null);

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        const analyticsResponse = await axiosInstance.get('/graph_analytics/sector-analytics');
        if (analyticsResponse.data.success) {
          setAnalyticsData(analyticsResponse.data.sectorAnalytics);
        } else {
          throw new Error('Failed to fetch analytics data');
        }
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, []);

  const toggleSector = (index) => {
    setExpandedSector(expandedSector === index ? null : index);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full p-8">
        <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="flex justify-center items-center h-full p-8">
        <div className="text-lg text-slate-600">No data available</div>
      </div>
    );
  }

  const sectorNames = analyticsData.map(sector => sector.sector);
  const totalInvestments = analyticsData.map(sector => sector.totalInvestment);
  const totalReturns = analyticsData.map(sector => sector.totalReturns);
  const profitLoss = analyticsData.map(sector => sector.profitLoss);
  const qtyLeftToSell = analyticsData.map(sector => sector.totalQtyLeftToSell);

  const stockNames = analyticsData.flatMap(sector => sector.stocks.map(stock => stock.stockName));
  const stockInvestments = analyticsData.flatMap(sector => sector.stocks.map(stock => stock.totalInvestment || 0));
  const stockProfitLoss = analyticsData.flatMap(sector => sector.stocks.map(stock => stock.profitLoss || 0));

  // Modern gradient color palette
  const pieColors = [
    'rgba(99, 102, 241, 0.8)',   // Indigo
    'rgba(139, 92, 246, 0.8)',   // Purple  
    'rgba(34, 197, 94, 0.8)',    // Green
    'rgba(239, 68, 68, 0.8)',    // Red
    'rgba(245, 158, 11, 0.8)',   // Amber
    'rgba(236, 72, 153, 0.8)',   // Pink
    'rgba(14, 165, 233, 0.8)',   // Sky
  ];
  const pieHoverColors = [
    'rgba(99, 102, 241, 1)',
    'rgba(139, 92, 246, 1)', 
    'rgba(34, 197, 94, 1)',
    'rgba(239, 68, 68, 1)',
    'rgba(245, 158, 11, 1)',
    'rgba(236, 72, 153, 1)',
    'rgba(14, 165, 233, 1)',
  ];
  const barColors = ['rgba(99, 102, 241, 0.8)', 'rgba(139, 92, 246, 0.8)']; 
  
  // Enhanced chart options with zoom and modern styling
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: { 
        position: 'top', 
        labels: { 
          font: { family: 'Inter', size: 13, weight: '500' }, 
          color: '#1e293b',
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 24,
          boxWidth: 12,
          boxHeight: 12,
        } 
      },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        titleFont: { family: 'Inter', size: 15, weight: '600' },
        bodyFont: { family: 'Inter', size: 13, weight: '400' },
        padding: 16,
        borderColor: 'rgba(99, 102, 241, 0.5)',
        borderWidth: 2,
        boxPadding: 8,
        usePointStyle: true,
        cornerRadius: 12,
        displayColors: true,
        callbacks: {
          title: (context) => {
            return context[0].label || '';
          },
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y || context.parsed || 0;
            return `  ${label}: ₹${value.toLocaleString('en-IN')}`;
          },
          footer: (context) => {
            return 'Hover for detailed information • Click legend to toggle';
          }
        },
      },
    },
    elements: {
      line: {
        tension: 0.4,
        borderWidth: 4,
        borderCapStyle: 'round',
        borderJoinStyle: 'round',
      },
      point: {
        radius: 6,
        hitRadius: 15,
        hoverRadius: 8,
        borderWidth: 3,
        hoverBorderWidth: 4,
      },
      arc: {
        borderWidth: 3,
        borderColor: '#ffffff',
        hoverOffset: 20,
        hoverBorderWidth: 4,
      },
      bar: {
        borderWidth: 0,
        borderRadius: 12,
        borderSkipped: false,
      }
    },
    scales: {
      x: { 
        ticks: { 
          font: { family: 'Inter', size: 12, weight: '500' }, 
          color: '#64748b',
          padding: 8,
        }, 
        grid: { 
          color: 'rgba(148, 163, 184, 0.15)', 
          drawBorder: false,
          lineWidth: 1,
        },
        border: {
          display: false
        } 
      },
      y: {
        ticks: {
          font: { family: 'Inter', size: 12, weight: '500' },
          color: '#64748b',
          padding: 12,
          callback: (value) => `₹${(value / 1000).toFixed(0)}K`
        },
        grid: { 
          color: 'rgba(148, 163, 184, 0.15)', 
          drawBorder: false,
          lineWidth: 1,
        },
        border: {
          display: false
        },
        beginAtZero: true,
      },
    },
    onHover: (event, activeElements) => {
      event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
    },
  };

  const stockPerformanceData = {
    datasets: analyticsData.map((sector, sectorIndex) => ({
      label: sector.sector,
      data: sector.stocks.map(stock => ({
        x: stock.totalInvestment,
        y: stock.profitLoss,
        r: Math.sqrt(stock.totalBoughtQty) / 2, // Size based on quantity
        stockName: stock.stockName
      })),
      backgroundColor: `hsla(${(sectorIndex * 40) % 360}, 70%, 60%, 0.7)`,
      borderColor: `hsla(${(sectorIndex * 40) % 360}, 70%, 50%, 1)`,
      borderWidth: 1
    }))
  };

  const stockPerformanceOptions = {
    ...chartOptions,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Total Investment (₹)',
          font: { family: 'Inter', size: 12, weight: '500' }
        },
        ticks: {
          callback: value => `₹${(value / 1000).toFixed(0)}K`
        }
      },
      y: {
        title: {
          display: true,
          text: 'Profit/Loss (₹)',
          font: { family: 'Inter', size: 12, weight: '500' }
        },
        ticks: {
          callback: value => `₹${(value / 1000).toFixed(0)}K`
        }
      }
    },
    plugins: {
      ...chartOptions.plugins,
      tooltip: {
        callbacks: {
          label: (context) => {
            const stock = context.raw;
            return [
              `Stock: ${stock.stockName}`,
              `Investment: ₹${stock.x.toLocaleString()}`,
              `P/L: ₹${stock.y.toLocaleString()}`,
              `Quantity: ${Math.round(stock.r * stock.r * 4)}`
            ];
          }
        }
      },
      legend: {
        position: 'right',
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          padding: 15,
          font: { family: 'Inter', size: 11 }
        }
      }
    }
  };

  const pieData = {
    labels: sectorNames,
    datasets: [{
      data: totalInvestments,
      backgroundColor: pieColors,
      hoverBackgroundColor: pieHoverColors,
      borderWidth: 3,
      borderColor: '#ffffff',
      hoverBorderWidth: 4,
      hoverOffset: 20,
      cutout: 0,
    }],
  };

  const barData = {
    labels: sectorNames,
    datasets: [
      { 
        label: 'Investment', 
        data: totalInvestments, 
        backgroundColor: 'rgba(99, 102, 241, 0.8)',
        borderColor: 'rgba(99, 102, 241, 1)',
        borderWidth: 2,
        borderRadius: 12,
        borderSkipped: false,
      },
      { 
        label: 'Returns', 
        data: totalReturns, 
        backgroundColor: 'rgba(139, 92, 246, 0.8)',
        borderColor: 'rgba(139, 92, 246, 1)', 
        borderWidth: 2,
        borderRadius: 12,
        borderSkipped: false,
      },
    ],
  };

  const lineData = {
    labels: sectorNames,
    datasets: [{
      label: 'Profit/Loss',
      data: profitLoss,
      fill: true,
      backgroundColor: 'rgba(99, 102, 241, 0.1)',
      borderColor: 'rgba(99, 102, 241, 1)',
      borderWidth: 4,
      tension: 0.4,
      pointBackgroundColor: '#ffffff',
      pointBorderColor: 'rgba(99, 102, 241, 1)',
      pointBorderWidth: 3,
      pointRadius: 6,
      pointHoverRadius: 8,
      pointHoverBackgroundColor: 'rgba(99, 102, 241, 1)',
      pointHoverBorderColor: '#ffffff',
      pointHoverBorderWidth: 4,
    }],
  };

  const doughnutData = {
    labels: sectorNames,
    datasets: [{
      data: qtyLeftToSell,
      backgroundColor: pieColors,
      hoverBackgroundColor: pieHoverColors,
      borderWidth: 3,
      borderColor: '#ffffff',
      hoverBorderWidth: 4,
      hoverOffset: 20,
      cutout: '65%',
    }],
  };

  return (
    <div style={{ all: 'unset', display: 'block', fontFamily: 'inherit' }}>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/20 to-purple-50/20 p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent tracking-tight">
                Portfolio Analytics
              </h1>
              <p className="text-slate-600 mt-2 text-lg">Comprehensive insights into your investment performance</p>
            </div>
                        <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" className="text-indigo-600 border-indigo-300 hover:bg-indigo-50">
                <RotateCcw className="w-4 h-4 mr-2" />
                Refresh Data
              </Button>
              <Button variant="outline" size="sm" className="text-purple-600 border-purple-300 hover:bg-purple-50">
                <TrendingUp className="w-4 h-4 mr-2" />
                Export Report
              </Button>
            </div>
          </div>
        </div>

        {/* Welcome Card */}
        <Card className="mb-8 bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 text-white border-0 shadow-2xl overflow-hidden relative backdrop-blur-xl">
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-indigo-500/40 to-purple-600/40 rounded-full -mr-20 -mt-20"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-purple-500/20 to-indigo-500/20 rounded-full -ml-16 -mb-16"></div>
          <CardHeader className="relative z-10 p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex-1">
                <CardTitle className="text-3xl text-white mb-3 font-bold tracking-tight flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  Advanced Portfolio Analytics
                </CardTitle>
                <p className="text-slate-200 text-lg leading-relaxed">
                  Modern interactive charts with detailed insights and beautiful visualizations. Hover over elements for detailed information and click legend items to toggle data.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-2 rounded-xl text-sm font-semibold shadow-lg ring-2 ring-indigo-400/30 backdrop-blur-sm">
                  <Activity className="w-4 h-4 inline mr-2" />
                  Interactive Mode
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{analyticsData?.length || 0}</div>
                  <div className="text-xs text-slate-300">Active Sectors</div>
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="group shadow-xl border-0 bg-gradient-to-br from-white via-indigo-50/30 to-purple-50/30 backdrop-blur-sm ring-1 ring-indigo-200/30 hover:shadow-2xl hover:ring-indigo-300/50 transition-all duration-300">
            <CardHeader className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border-b border-indigo-200/30">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3 text-slate-900">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <PieChart className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xl font-bold tracking-tight">Sector Investment</span>
                </CardTitle>
                <Button variant="ghost" size="sm" className="text-indigo-600 hover:bg-indigo-50">
                  <Maximize2 className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="h-80 relative">
              <Pie data={pieData} options={chartOptions} />
                                <div className="absolute bottom-2 right-2 text-xs text-slate-500 bg-white/80 px-2 py-1 rounded">
                  Interactive chart
            </div>
          </div>
            </CardContent>
          </Card>

          <Card className="group shadow-xl border-0 bg-gradient-to-br from-white via-indigo-50/30 to-purple-50/30 backdrop-blur-sm ring-1 ring-indigo-200/30 hover:shadow-2xl hover:ring-indigo-300/50 transition-all duration-300">
            <CardHeader className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border-b border-indigo-200/30">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3 text-slate-900">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <BarChart3 className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xl font-bold tracking-tight">Investment vs Returns</span>
                </CardTitle>
                <Button variant="ghost" size="sm" className="text-indigo-600 hover:bg-indigo-50">
                  <Maximize2 className="w-4 h-4" />
                </Button>
            </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="h-80 relative">
              <Bar data={barData} options={chartOptions} />
                                <div className="absolute bottom-2 right-2 text-xs text-slate-500 bg-white/80 px-2 py-1 rounded">
                  Hover for details
                </div>
          </div>
            </CardContent>
          </Card>

          <Card className="group shadow-xl border-0 bg-gradient-to-br from-white via-indigo-50/30 to-purple-50/30 backdrop-blur-sm ring-1 ring-indigo-200/30 hover:shadow-2xl hover:ring-indigo-300/50 transition-all duration-300">
            <CardHeader className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border-b border-indigo-200/30">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3 text-slate-900">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <LineChart className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xl font-bold tracking-tight">Profit & Loss Trend</span>
                </CardTitle>
                <Button variant="ghost" size="sm" className="text-indigo-600 hover:bg-indigo-50">
                  <Maximize2 className="w-4 h-4" />
                </Button>
            </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="h-80 relative">
              <Line data={lineData} options={chartOptions} />
                                <div className="absolute bottom-2 right-2 text-xs text-slate-500 bg-white/80 px-2 py-1 rounded">
                  Trend analysis
                </div>
          </div>
            </CardContent>
          </Card>

          <Card className="group shadow-xl border-0 bg-gradient-to-br from-white via-indigo-50/30 to-purple-50/30 backdrop-blur-sm ring-1 ring-indigo-200/30 hover:shadow-2xl hover:ring-indigo-300/50 transition-all duration-300">
            <CardHeader className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border-b border-indigo-200/30">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3 text-slate-900">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Activity className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xl font-bold tracking-tight">Quantity to Sell</span>
                </CardTitle>
                <Button variant="ghost" size="sm" className="text-indigo-600 hover:bg-indigo-50">
                  <Maximize2 className="w-4 h-4" />
                </Button>
            </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="h-80 relative">
              <Doughnut data={doughnutData} options={chartOptions} />
                <div className="absolute bottom-2 right-2 text-xs text-slate-500 bg-white/80 px-2 py-1 rounded">
                  Hover for details
            </div>
          </div>
            </CardContent>
          </Card>
        </div>

                  {/* Stock Performance Bubble Chart */}
        <Card className="mb-8 group shadow-xl border-0 bg-gradient-to-br from-white via-indigo-50/30 to-purple-50/30 backdrop-blur-sm ring-1 ring-indigo-200/30 hover:shadow-2xl hover:ring-indigo-300/50 transition-all duration-300">
          <CardHeader className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border-b border-indigo-200/30">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-3 text-slate-900">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Grid3X3 className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold tracking-tight">Stock Performance Matrix</span>
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" className="text-indigo-600 hover:bg-indigo-50">
                  <Activity className="w-4 h-4 mr-2" />
                  Analyze
                </Button>
                <Button variant="ghost" size="sm" className="text-purple-600 hover:bg-purple-50">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Trends
                </Button>
              </div>
          </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="h-96 relative">
            <Bubble data={stockPerformanceData} options={stockPerformanceOptions} />
                            <div className="absolute bottom-2 left-2 text-xs text-slate-500 bg-white/80 px-2 py-1 rounded">
                Bubble size = Quantity • X-axis = Investment • Y-axis = P/L
              </div>
        </div>
          </CardContent>
        </Card>

        {/* Sector-wise Breakdown */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Activity className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent tracking-tight">
              Detailed Sector Analysis
            </h2>
          </div>
          <div className="space-y-4">
          {analyticsData.map((sector, index) => (
              <Card 
              key={index} 
                className={`group shadow-lg border-0 bg-gradient-to-br from-white via-indigo-50/20 to-purple-50/20 backdrop-blur-sm cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-[1.02] ${
                  expandedSector === index ? 'ring-2 ring-indigo-500 shadow-2xl' : 'hover:ring-1 hover:ring-indigo-300'
                }`}
              onClick={() => toggleSector(index)}
            >
                <CardHeader className="bg-gradient-to-r from-indigo-500/5 to-purple-500/5 border-b border-indigo-200/20">
                  <CardTitle className="flex items-center justify-between text-slate-900">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <span className="text-white font-bold text-sm">{sector.sector.charAt(0)}</span>
                      </div>
                      <span className="text-xl font-bold tracking-tight">{sector.sector}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-sm text-slate-600 bg-slate-100 px-2 py-1 rounded-full">
                        {sector.totalStocks} stocks
                  </div>
                      {expandedSector === index ? <ChevronUp className="w-5 h-5 text-indigo-600" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </div>
                  </CardTitle>
                </CardHeader>
                  {expandedSector === index && (
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                        <div className="text-center p-3 bg-slate-50 rounded-lg">
                          <div className="text-xs text-slate-600 mb-1">Total Stocks</div>
                          <div className="text-lg font-semibold text-slate-900">{sector.totalStocks}</div>
                  </div>
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <div className="text-xs text-slate-600 mb-1">Investment</div>
                          <div className="text-lg font-semibold text-blue-700">₹{sector.totalInvestment.toLocaleString()}</div>
                  </div>
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <div className="text-xs text-slate-600 mb-1">Returns</div>
                          <div className="text-lg font-semibold text-blue-700">₹{sector.totalReturns.toLocaleString()}</div>
                  </div>
                        <div className={`text-center p-3 rounded-lg ${sector.profitLoss >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                          <div className="text-xs text-slate-600 mb-1">Profit/Loss</div>
                          <div className={`text-lg font-semibold ${sector.profitLoss >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            ₹{sector.profitLoss.toLocaleString()}
                  </div>
                </div>
                        <div className="text-center p-3 bg-slate-50 rounded-lg">
                          <div className="text-xs text-slate-600 mb-1">Qty to Sell</div>
                          <div className="text-lg font-semibold text-slate-900">{sector.totalQtyLeftToSell}</div>
                      </div>
                        <div className="text-center p-3 bg-slate-50 rounded-lg">
                          <div className="text-xs text-slate-600 mb-1">Qty to Buy</div>
                          <div className="text-lg font-semibold text-slate-900">{sector.totalQtyLeftToBuy}</div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <h4 className="font-semibold text-slate-900 mb-3">Stocks in this sector:</h4>
                        {sector.stocks.map((stock, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div>
                              <h5 className="font-medium text-slate-900">{stock.stockName}</h5>
                              <div className="text-sm text-slate-600">Quantity: {stock.qtyLeftToSell}</div>
                    </div>
                            <div className={`text-lg font-semibold ${stock.profitLoss >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                              ₹{stock.profitLoss.toLocaleString()}
              </div>
            </div>
          ))}
        </div>
                    </CardContent>
                  )}
                </Card>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;